/**
 * Recovers from a screen chunk that 404s after a deploy.
 *
 * Every screen is code-split, so its filename carries a content hash
 * (SRV210-P99izfCo.js). A deploy writes new hashes and deletes the old files. nginx
 * serves index.html with no-store, so a *fresh* page load always gets the new module
 * graph -- but a tab that was already open still holds the old one, and the next SPA
 * navigation asks for a hash that no longer exists. The dynamic import rejects,
 * ErrorBoundary catches it, and the user is left on "Something went wrong."
 *
 * Reloading fetches the new index.html and the new chunk names, which is the only thing
 * that can fix an already-loaded tab.
 *
 * Kept dependency-free and in its own module so it can be unit tested: componentUtil.jsx
 * pulls in @loadable/component, exceljs and a CSS module, none of which load under
 * `node --test`.
 */

/** sessionStorage key guarding the one-shot reload. */
export const CHUNK_RELOAD_KEY = "maxis:chunk-reload";

/**
 * Was this "the chunk is not on the server", rather than an error thrown by the screen's
 * own module code? Browsers word it differently, so match all of them.
 */
export const isChunkLoadError = (error) => {
  const message = String(error?.message || error || "");
  return (
    /Failed to fetch dynamically imported module/i.test(message) || // Chrome, Edge
    /error loading dynamically imported module/i.test(message) ||   // Firefox
    /Importing a module script failed/i.test(message) ||            // Safari
    /ChunkLoadError/i.test(message)
  );
};

/**
 * How long after a reload another chunk failure is treated as "the reload did not help".
 *
 * A timestamp, not a boolean-cleared-on-success. Clearing the flag whenever any module
 * loaded worked looked right and reload-looped in testing: after each reload the app's
 * other modules load fine, wiping the flag before the broken chunk is reached again.
 * Measured 5 reloads against a permanently-404 chunk before this became a window.
 */
export const RELOAD_WINDOW_MS = 10_000;

/**
 * Wraps a dynamic-import factory so a stale chunk reloads the page once.
 *
 * A failure inside the window means the reload already happened and did not fix it --
 * the chunk is genuinely gone (a broken deploy), so surface the error rather than loop.
 * A failure after the window is a fresh event, e.g. a second deploy in a long-lived tab,
 * and earns its own reload.
 *
 * @param {() => Promise<any>} load
 * @returns {() => Promise<any>}
 */
export const withStaleChunkReload = (load) => () =>
  load().catch((error) => {
    if (!isChunkLoadError(error)) throw error;

    let lastReloadAt = 0;
    try {
      lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
    } catch (e) {
      // No sessionStorage means no guard, and an unguarded reload can loop forever.
      // Surfacing the error is the safer failure.
      throw error;
    }
    if (Date.now() - lastReloadAt < RELOAD_WINDOW_MS) throw error;

    try {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch (e) {
      throw error;
    }

    window.location.reload();
    // Stay pending: resolving or rejecting here would flash the error UI in the
    // moments before the reload actually takes effect.
    return new Promise(() => {});
  });
